import React, { useEffect, useRef, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  ChevronRight,
  User,
  Settings,
  LifeBuoy,
  LogOut,
  Check,
} from "lucide-react";
import { ThemeContext } from "../context/ThemeContext";
import { setSidebarCollapsed } from "../store/chatSlice";
import "../style/settings-popup.css";

export default function SettingsPopup({
  isOpen,
  onClose,
  user,
  userPlanName = "Go",
  transliteratedUserName,
  onLogout,
  onOpenHelp,
  collapsed = false,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cardRef = useRef(null);
  const { theme, setTheme } = useContext(ThemeContext);
  const [themePopOpen, setThemePopOpen] = useState(false);

  // Reset theme pop box when main popup closes
  useEffect(() => {
    if (!isOpen) {
      setThemePopOpen(false);
    }
  }, [isOpen]);

  // Close on ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, onClose]);

  // Close on click outside
  useEffect(() => {
    const handleMouseDown = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) {
        const isSettingsBtn =
          e.target.closest(".settings-icon-wrapper") ||
          e.target.closest(".collapsed-settings-btn");
        if (!isSettingsBtn) {
          onClose();
        }
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleMouseDown);
      return () => document.removeEventListener("mousedown", handleMouseDown);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const displayName = transliteratedUserName || user?.name || "Sharib";
  const displayPlan = userPlanName || "Go";

  const handleProfileClick = () => {
    onClose();
    dispatch(setSidebarCollapsed(true));
    if (user) {
      navigate("/account");
    } else {
      navigate("/login");
    }
  };

  const handleUpgradeClick = () => {
    onClose();
    dispatch(setSidebarCollapsed(true));
    navigate("/subscription");
  };

  const handleToggleThemePop = (e) => {
    e.stopPropagation();
    setThemePopOpen((prev) => !prev);
  };

  const handleSelectTheme = (selectedTheme, e) => {
    e.stopPropagation();
    setTheme(selectedTheme);
    onClose();
  };

  const handleAccountClick = () => {
    onClose();
    dispatch(setSidebarCollapsed(true));
    if (user) {
      navigate("/account");
    } else {
      navigate("/login");
    }
  };

  const handleSettingsClick = () => {
    onClose();
    dispatch(setSidebarCollapsed(true));
    if (user?.role === "admin") {
      navigate("/admin/settings");
    }
  };

  const handleHelpClick = () => {
    onClose();
    if (onOpenHelp) {
      onOpenHelp();
    }
  };

  const handleLogoutClick = () => {
    onClose();
    if (onLogout) {
      onLogout();
    }
  };

  return (
    <div
      ref={cardRef}
      className={`settings-popup-card ${
        collapsed ? "settings-popup-collapsed" : "settings-popup-expanded"
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* ── Top Profile Row: Avatar, Name, Plan, Chevron ── */}
      <button
        type="button"
        className="sp-profile-row"
        onClick={handleProfileClick}
        title="View Profile"
      >
        <div className="sp-profile-avatar">
          {user?.profilePicture || user?.avatar ? (
            <img
              src={user.profilePicture || user.avatar}
              alt={displayName}
              referrerPolicy="no-referrer"
            />
          ) : (
            <span>{displayName?.[0]?.toUpperCase() || "S"}</span>
          )}
        </div>

        <div className="sp-profile-info">
          <span className="sp-profile-name">{displayName}</span>
          <span className="sp-profile-plan">{displayPlan}</span>
        </div>

        <ChevronRight size={16} className="sp-profile-chevron" />
      </button>

      {/* ── Divider 1 ── */}
      <div className="sp-divider" />

      {/* ── Middle Action Items ── */}
      <div className="sp-menu-list">
        {/* Upgrade plan */}
        <button
          type="button"
          className="sp-item"
          onClick={handleUpgradeClick}
        >
          <span className="sp-item-icon">
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="sp-svg-icon"
            >
              <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
            </svg>
          </span>
          <span className="sp-item-label">Upgrade plan</span>
        </button>

        {/* Theme (with pop box flyout) */}
        <div className="sp-theme-wrapper">
          <button
            type="button"
            className={`sp-item ${themePopOpen ? "sp-item-active" : ""}`}
            onClick={handleToggleThemePop}
          >
            <span className="sp-item-icon">
              {/* Custom circular theme badge */}
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="sp-svg-icon"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 2a7 7 0 1 0 10 10" strokeDasharray="2 2" />
                <path d="M12 6v6l4 2" />
              </svg>
            </span>
            <span className="sp-item-label">Theme</span>
          </button>

          {/* Theme Sub-Popup Flyout Box */}
          {themePopOpen && (
            <div className="sp-theme-flyout" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="sp-theme-option"
                onClick={(e) => handleSelectTheme("system", e)}
              >
                <span>System</span>
                {theme === "system" && <Check size={16} className="sp-theme-check" />}
              </button>

              <button
                type="button"
                className="sp-theme-option"
                onClick={(e) => handleSelectTheme("light", e)}
              >
                <span>Light</span>
                {theme === "light" && <Check size={16} className="sp-theme-check" />}
              </button>

              <button
                type="button"
                className="sp-theme-option"
                onClick={(e) => handleSelectTheme("dark", e)}
              >
                <span>Dark</span>
                {theme === "dark" && <Check size={16} className="sp-theme-check" />}
              </button>
            </div>
          )}
        </div>

        {/* Profile */}
        <button
          type="button"
          className="sp-item"
          onClick={handleAccountClick}
        >
          <span className="sp-item-icon">
            <User size={17} />
          </span>
          <span className="sp-item-label">Profile</span>
        </button>

        {/* Settings — only shown to admins who have a real /admin/settings page */}
        {user?.role === "admin" && (
          <button
            type="button"
            className="sp-item"
            onClick={handleSettingsClick}
          >
            <span className="sp-item-icon">
              <Settings size={17} />
            </span>
            <span className="sp-item-label">Settings</span>
          </button>
        )}
      </div>

      {/* ── Divider 2 ── */}
      <div className="sp-divider" />

      {/* ── Bottom Items: Help & Log out ── */}
      <div className="sp-menu-list">
        {/* Help */}
        <button
          type="button"
          className="sp-item"
          onClick={handleHelpClick}
        >
          <span className="sp-item-icon">
            <LifeBuoy size={17} />
          </span>
          <span className="sp-item-label">Help</span>
          <ChevronRight size={16} className="sp-item-chevron" />
        </button>

        {/* Log out */}
        <button
          type="button"
          className="sp-item"
          onClick={handleLogoutClick}
        >
          <span className="sp-item-icon">
            <LogOut size={17} />
          </span>
          <span className="sp-item-label">Log out</span>
        </button>
      </div>
    </div>
  );
}
